<script setup lang="ts">

import {useContext, useDialogs, useHost} from "@hantera/portal-app";
import {DropdownDivider, DropdownItem, DropdownMenu, IconHamburger} from "@hantera/design-system";
import {computed, useTemplateRef} from "vue";
import useWorkspace from "@/composition/useWorkspace";

const context = useContext()
const profileMenuButton = useTemplateRef<HTMLDivElement>('profileMenuButton')
const profileMenu = useTemplateRef<InstanceType<typeof DropdownMenu>>('profileMenu')
const host = useHost()
const dialogs = useDialogs()
const workspace = useWorkspace()

const isLocalDevEnabled = (<any>window).__HANTERA_APP_DEVELOPMENT__;

const hasPicture = computed(() => {
  const user = context.user();
  if (!user?.picture?.length) {
    return false;
  }
  return user.picture.length > 0
});

const pictureInitials = computed(() => {
  if (typeof context.user().name === 'string') {

    const [firstName, lastName] = context.user().name?.split(' ')
    return `${firstName[0]}${lastName?.[0] || ''}`
  }

  return ''
});

function openMenu() {
  profileMenu.value?.open()
}

function setTheme(value: 'system' | 'light' | 'dark') {
  (window as any).setTheme(value)
}

async function signOut() {

  if (await dialogs.confirm('Are you sure?', 'Sign out', "Yes", "No", true)) {
    host.send({
      type: 'signOut'
    })
  }
}

function toggleLocalDev() {
  if (isLocalDevEnabled) {
    host.send({
      type: 'disableAppDev'
    });
  } else {
    host.send({
      type: 'enableAppDev'
    });
  }
}

function openPat() {
  workspace.openViewTab('/pat', 'Personal Access Tokens')
}

function openUserSettings() {
  workspace.openViewTab('/user', 'User Settings')
}

</script>

<template>
  <div class="profile-bar">
    <div v-if="hasPicture" class="profile-bar-picture">
      <img :src="context.user()?.picture" alt="Profile Picture" />
    </div>
    <div v-else class="profile-bar-picture-initials">
      {{ pictureInitials }}
    </div>

    <div class="profile-bar-name">{{ context.user()?.name }}</div>
    
    <button class="btn-silent profile-menu-button" ref="profileMenuButton" @click="openMenu"><IconHamburger /></button>
  </div>

  <DropdownMenu style="width: 40px; height: 40px" ref="profileMenu" :target="profileMenuButton">
    <DropdownItem @click="openUserSettings">User Settings</DropdownItem>
    <DropdownItem @click="openPat">Personal Access Tokens (PAT)</DropdownItem>
    <DropdownItem @click="toggleLocalDev">{{ isLocalDevEnabled ? 'Disable' : 'Enable' }} local development</DropdownItem>
    <DropdownDivider/>
    <DropdownItem @click="setTheme('system')">Switch to system theme</DropdownItem>
    <DropdownItem @click="setTheme('light')">Switch to light theme</DropdownItem>
    <DropdownItem @click="setTheme('dark')">Switch to dark theme</DropdownItem>
    <DropdownDivider/>
    <DropdownItem @click="signOut">Sign out</DropdownItem>
  </DropdownMenu>
</template>

<style scoped>
.profile-bar {
  border-top: 1px solid hsl(var(--color-80));

  display: grid;
  grid-template-columns: min-content auto min-content;
  align-items: center;
  column-gap: 10px;
  padding: 10px;

  box-shadow: 0 0 10px 5px var(--color-shadow);
}

.profile-bar-picture {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.profile-bar-picture img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.profile-bar-picture-initials {
  background-color: hsl(var(--color-70));
  color: hsl(var(--color-10));
  border-radius: 50%;
  user-select: none;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: .8rem;
  font-weight: 500;
  line-height: 1em;
  height: 22px;
  width: 22px;
  text-transform: uppercase;
}

.profile-menu-button {
  width: 22px;
  height: 22px;
  padding: 0;
  --btn-border-radius: 4px;
}

.profile-menu-button svg {
  width: 10px;
}

.profile-bar-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
